import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Scan } from 'lucide-react';

const BarcodeScanner = ({ onScanSuccess }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);

      html5QrCodeRef.current = new Html5Qrcode('barcode-reader');

      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          stopScanning();
          onScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (happens frequently)
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current && scanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  return (
    <div className="space-y-4">
      <div
        id="barcode-reader"
        data-testid="barcode-reader"
        className="w-full rounded-lg overflow-hidden bg-gray-100"
        style={{ minHeight: scanning ? '300px' : '100px' }}
      ></div>

      {error && (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg" data-testid="scanner-error">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        {!scanning ? (
          <Button
            onClick={startScanning}
            data-testid="start-scanning-button"
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
          >
            <Scan className="w-4 h-4 mr-2" />
            Start Scanning
          </Button>
        ) : (
          <Button
            onClick={stopScanning}
            data-testid="stop-scanning-button"
            variant="outline"
            className="w-full border-red-500 text-red-600 hover:bg-red-50"
          >
            Stop Scanning
          </Button>
        )}
      </div>

      <p className="text-sm text-gray-500 text-center">
        Position the barcode within the frame to scan
      </p>
    </div>
  );
};

export default BarcodeScanner;
